/**
 * Debug logging for supplier quote PDF extraction (Rhenium-focused).
 */

import { logInfo } from '../../config_and_helpers/logger';
import {
  ENCO_QUOTE_FORMAT_ID,
  isEncoQuoteFormat,
  isRheniumQuoteFormat,
} from '../quoteExtractionValidation';

export const RHENIUM_QUOTE_FORMAT_ID = 'rhenium';
const LOG_CONTEXT = 'logQuoteExtraction';

const isProduction = () =>
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

/**
 * @param {*} value
 * @returns {boolean}
 */
function isBlank(value) {
  return value == null || String(value).trim() === '';
}

/**
 * @param {*} value
 * @returns {number|null}
 */
function toNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Object} obj
 * @param {number} [maxKeys]
 * @returns {string[]}
 */
export function sampleObjectKeys(obj, maxKeys = 40) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.keys(obj).slice(0, maxKeys);
}

/**
 * Resolve nested API payload (handles `data` / double `data` wrappers).
 *
 * @param {Object} raw
 * @returns {{ source: Object, wrappers: string[] }}
 */
export function resolveQuoteExtractionSource(raw) {
  /** @type {string[]} */
  const wrappers = [];
  let source = raw?.data && typeof raw.data === 'object' ? raw.data : raw || {};
  if (raw?.data && typeof raw.data === 'object') wrappers.push('data');

  const nested =
    source.data && typeof source.data === 'object' ? source.data : null;
  const hasTopLevelQuoteShape =
    source.supplier != null ||
    source.quote != null ||
    source.items != null ||
    source.line_items != null ||
    source.lineItems != null;

  if (nested && !hasTopLevelQuoteShape) {
    source = nested;
    wrappers.push('data.data');
  }

  return { source, wrappers };
}

/**
 * Pick line items array from API source using known aliases.
 *
 * @param {Object} source
 * @returns {{ items: Object[], sourceKey: string|null }}
 */
export function resolveQuoteExtractionItems(source) {
  if (!source || typeof source !== 'object') {
    return { items: [], sourceKey: null };
  }

  const candidates = [
    ['items', source.items],
    ['line_items', source.line_items],
    ['lineItems', source.lineItems],
    ['quote_items', source.quote_items],
    ['quoteItems', source.quoteItems],
    ['products', source.products],
  ];

  for (const [key, value] of candidates) {
    if (Array.isArray(value) && value.length > 0) {
      return { items: value, sourceKey: key };
    }
  }

  for (const [key, value] of candidates) {
    if (Array.isArray(value)) {
      return { items: value, sourceKey: key };
    }
  }

  return { items: [], sourceKey: null };
}

/**
 * @param {Object} rawBody - Full axios `response.data` or error body
 * @returns {Object}
 */
export function buildRawQuoteApiSnapshot(rawBody) {
  const { source, wrappers } = resolveQuoteExtractionSource(rawBody || {});
  const { items, sourceKey: itemsSourceKey } = resolveQuoteExtractionItems(source);
  const quote = source.quote && typeof source.quote === 'object' ? source.quote : {};
  const totals = source.totals && typeof source.totals === 'object' ? source.totals : {};
  const meta = source.meta && typeof source.meta === 'object' ? source.meta : {};
  const supplier =
    source.supplier && typeof source.supplier === 'object' ? source.supplier : {};
  const customer =
    source.customer && typeof source.customer === 'object' ? source.customer : {};

  const firstItem = items[0];
  return {
    topLevelKeys: sampleObjectKeys(rawBody),
    wrappers,
    sourceKeys: sampleObjectKeys(source),
    meta: {
      supplierFormat:
        meta.supplierFormat ?? meta.supplier_format ?? meta.parserFormat ?? meta.parser_format,
      parserVersion: meta.parserVersion ?? meta.parser_version ?? null,
    },
    quoteKeys: sampleObjectKeys(quote),
    totalsKeys: sampleObjectKeys(totals),
    itemsSourceKey,
    itemCount: items.length,
    firstItemKeys:
      firstItem && typeof firstItem === 'object' ? sampleObjectKeys(firstItem) : [],
    firstItemPreview:
      firstItem && typeof firstItem === 'object'
        ? {
            catalog_number: firstItem.catalog_number ?? firstItem.catalogNumber ?? null,
            product_number: firstItem.product_number ?? firstItem.productNumber ?? null,
            description: firstItem.description ?? null,
            quantity: firstItem.quantity ?? null,
            unit_price: firstItem.unit_price ?? firstItem.unitPrice ?? null,
            line_total: firstItem.line_total ?? firstItem.lineTotal ?? null,
          }
        : null,
    quoteFieldPreview: {
      quote_number: quote.quote_number ?? quote.quoteNumber ?? null,
      quote_date: quote.quote_date ?? quote.quoteDate ?? null,
      valid_until: quote.valid_until ?? quote.validUntil ?? null,
      payment_terms: quote.payment_terms ?? quote.paymentTerms ?? null,
      vat_amount: quote.vat_amount ?? quote.vatAmount ?? totals.vat_amount ?? totals.vatAmount ?? null,
      total: quote.total ?? totals.total ?? null,
      currency: quote.currency ?? totals.currency ?? null,
    },
    apiWarnings: Array.isArray(source.warnings)
      ? source.warnings.map(w => ({
          field: w.field,
          message: w.message,
          severity: w.severity,
        }))
      : [],
    rawTextLength:
      typeof meta.rawText === 'string'
        ? meta.rawText.length
        : typeof meta.raw_text === 'string'
          ? meta.raw_text.length
          : 0,
    customerPreview: {
      name: customer.name ?? null,
      company_id: customer.companyId ?? customer.company_id ?? null,
    },
    supplierPreview: {
      name: supplier.name ?? null,
      tax_id: supplier.taxId ?? supplier.tax_id ?? null,
      website: supplier.website ?? null,
    },
  };
}

/**
 * @param {import('../../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {Object}
 */
export function buildQuoteExtractionSnapshot(data) {
  if (!data) return { empty: true };

  return {
    meta: data.meta,
    supplier: {
      name: data.supplier?.name ?? null,
      website: data.supplier?.website ?? null,
      taxId: data.supplier?.taxId ?? null,
    },
    quote: data.quote,
    totals: data.totals,
    itemCount: data.items?.length ?? 0,
    items: (data.items || []).map((item, index) => ({
      index,
      lineNumber: item.lineNumber,
      catalogNumber: item.catalogNumber,
      productNumber: item.productNumber,
      description: item.description?.slice?.(0, 80) ?? item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      unitPriceAfterDiscount: item.unitPriceAfterDiscount,
      lineTotal: item.lineTotal,
      unitPriceCurrency: item.unitPriceCurrency,
      lineTotalCurrency: item.lineTotalCurrency,
    })),
    warnings: data.warnings,
  };
}

/**
 * Compare normalized draft against save validation expectations.
 *
 * @param {import('../../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {Object}
 */
export function auditQuoteExtractionFieldPopulation(data) {
  const items = data?.items || [];
  const isRhenium = isRheniumQuoteFormat(data);
  const isEnco = isEncoQuoteFormat(data);
  const lineTotalSum = items.reduce((sum, item) => {
    const lineTotal = toNumber(item.lineTotal);
    return lineTotal != null ? sum + lineTotal : sum;
  }, 0);
  const hasQuoteTotal = isEnco
    ? toNumber(data?.quote?.subtotal) != null
    : toNumber(data?.quote?.total) != null || (isRhenium && lineTotalSum > 0);

  const populated = {
    quoteNumber: !isBlank(data?.quote?.quoteNumber),
    quoteDate: !isBlank(data?.quote?.quoteDate),
    quoteTotal: hasQuoteTotal,
    vatAmount: !isBlank(data?.quote?.vatAmount),
    paymentTerms: !isBlank(data?.quote?.paymentTerms),
    validUntil: !isBlank(data?.quote?.validUntil),
    currency: !isBlank(data?.quote?.currency),
    supplierName: !isBlank(data?.supplier?.name),
    itemCount: items.length,
  };

  const missingBlocking = [];
  if (!populated.quoteNumber) missingBlocking.push('quote.quoteNumber');
  if (!isRhenium && !populated.quoteDate) missingBlocking.push('quote.quoteDate');
  if (!populated.quoteTotal) missingBlocking.push('quote.total');
  if (items.length === 0) missingBlocking.push('items[]');

  items.forEach((item, index) => {
    const rowLabel = `Row ${index + 1}`;
    const hasCatalog =
      !isBlank(item.catalogNumber) || !isBlank(item.productNumber);
    const hasDesc = !isBlank(item.description);
    if (!hasCatalog && !hasDesc) {
      missingBlocking.push(`${rowLabel}: catalog/description`);
    }
    if (toNumber(item.quantity) == null) {
      missingBlocking.push(`${rowLabel}: quantity`);
    }
    const hasPrice =
      toNumber(item.unitPriceAfterDiscount) != null ||
      toNumber(item.unitPrice) != null;
    const hasLineTotal = toNumber(item.lineTotal) != null;
    if (!hasPrice && !hasLineTotal) {
      missingBlocking.push(`${rowLabel}: unitPrice/lineTotal`);
    }
  });

  const missingWarnings = [];
  if (!populated.vatAmount) missingWarnings.push('quote.vatAmount');
  if (!populated.paymentTerms) missingWarnings.push('quote.paymentTerms');
  if (!populated.validUntil) missingWarnings.push('quote.validUntil');

  return { populated, missingBlocking, missingWarnings };
}

/**
 * @param {string} stage
 * @param {Object} payload
 */
export function logRheniumQuoteExtractionDebug(stage, payload = {}) {
  const {
    fileName = null,
    rawBody = null,
    normalized = null,
    validation = null,
    extra = {},
  } = payload;

  const formatId =
    normalized?.meta?.supplierFormat ??
    normalized?.meta?.parserFormat ??
    rawBody?.data?.meta?.supplierFormat ??
    rawBody?.meta?.supplierFormat ??
    null;

  const formatKey = String(formatId || '').toLowerCase();
  const isRhenium = formatKey === RHENIUM_QUOTE_FORMAT_ID;
  const isEnco = formatKey === ENCO_QUOTE_FORMAT_ID;
  const isSupportedClientFormat = isRhenium || isEnco;
  const rawSnapshot = rawBody ? buildRawQuoteApiSnapshot(rawBody) : null;
  const emptyExtraction =
    (rawSnapshot?.itemCount ?? normalized?.items?.length ?? 0) === 0;
  const pipelineStages = new Set([
    'after_normalize',
    'extract_success',
    'review_draft',
    'validation',
  ]);

  const shouldLog =
    isSupportedClientFormat ||
    (stage === 'api_raw' && (!formatId || emptyExtraction)) ||
    (pipelineStages.has(stage) && isSupportedClientFormat);

  if (!shouldLog) {
    return;
  }

  const audit = normalized ? auditQuoteExtractionFieldPopulation(normalized) : null;
  const normalizedSnapshot = normalized
    ? buildQuoteExtractionSnapshot(normalized)
    : null;

  const logPayload = {
    stage,
    fileName,
    formatId,
    raw: rawSnapshot,
    normalized: normalizedSnapshot,
    fieldAudit: audit,
    validation: validation
      ? {
          canSave: validation.canSave,
          blockingErrors: validation.blockingErrors,
          warningMessages: (validation.warnings || []).map(w => w.message),
        }
      : null,
    ...extra,
  };

  const formatLabel = isEnco
    ? 'ENCO'
    : isRhenium
      ? 'Rhenium'
      : 'Quote PDF';
  logInfo(LOG_CONTEXT, `[${formatLabel}] ${stage}`, logPayload);

  if (!isProduction()) {
    const label = isEnco
      ? 'ENCO quote extraction'
      : isRhenium
        ? 'Rhenium quote extraction'
        : 'Quote PDF extraction';
    console.group(`[${label}] ${stage}${fileName ? ` — ${fileName}` : ''}`);
    if (rawSnapshot) {
      console.info('Raw API snapshot', rawSnapshot);
    }
    if (normalizedSnapshot) {
      console.info('Normalized snapshot', normalizedSnapshot);
    }
    if (audit) {
      console.info('Field population audit', audit);
    }
    if (validation) {
      console.info('Validation result', validation);
    }
    console.groupEnd();
  }
}

/**
 * @param {string} context
 * @param {string} message
 * @param {import('../../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @param {Object} [extra]
 */
export function logQuoteExtraction(context, message, data, extra = {}) {
  logInfo(context, message, {
    ...extra,
    extraction: buildQuoteExtractionSnapshot(data),
    fieldAudit: auditQuoteExtractionFieldPopulation(data),
  });
}
